/**
 * The audience's size, at the top of the definition pane — the block that replaced the base-entity
 * card (ACT2-600).
 *
 * The grain that used to sit here ("One member = one <table>") did not earn the space: it is a
 * technical fact a technical reader wants, so it moved into the generated SQL as a comment, where that
 * reader is already looking. What takes its place is the number the whole page is about, stated once,
 * large, with the share of the universe it represents drawn as a bar.
 *
 * The bar is what makes an EDIT legible. A percentage in a corner tells you the audience is 2.3%; two
 * percentages a minute apart do not tell you what your last rule did. So when a recount lands and the
 * page still remembers the previous size, the bar splits: the part both counts agree on stays
 * `primary`, and the difference renders after it in `success` (the rule widened the audience) or
 * `danger` (it narrowed it), with a matching delta chip beside the number.
 *
 * The two directions are drawn at EQUAL weight. What separates them is position, not intensity: a gain
 * sits inside the filled part, because those members are in the audience now, while a loss extends past
 * it, because those members are gone. The loss was briefly drawn faded to say "no longer here", which
 * position already says — and fading it made a narrowing quieter than a widening, which is backwards.
 * A shrink is the change you most want to notice.
 *
 * That memory is SESSION-ONLY and deliberately so. It answers "what did the edit I just made do",
 * which is a question about the editing session in front of you — a stored previous count would need a
 * column and a migration to answer a question nobody asks after a reload.
 *
 * The page supplies it by watching the audience RECORD, not by capturing when its own refresh is
 * clicked. A count is not only something this control runs: the agent runs one too, and the page hears
 * about it as a refetch. Both arrive the same way, so both get a delta.
 *
 * The refresh moved here from the page header, and ACT2-604 freed it. It arrived carrying a
 * disabled-with-a-reason state, because `audience.count` counted the STORED definition by id and an open
 * draft would have measured rules that were no longer on screen. The count now takes the definition on
 * screen, so the control is live at all times and needs no reason and no tooltip.
 *
 * It is also always VISIBLE, which the header control it replaced was not — that one hid itself on a
 * fresh count. A recount is the action this block exists to offer, and a control that disappears exactly
 * when the number looks trustworthy is the harder thing to explain.
 */
import dayjs from 'dayjs';
import { RefreshCw, Users, X } from 'lucide-react';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { formatGrouped, timeAgo } from '@/_shared/lib/format';
import { cn } from '@/_shared/lib/utils';

import type { CountState } from '../types';
import { CountBadge } from './CountBadge';
import { StaleIndicator } from './StaleIndicator';

/**
 * A previously displayed size, kept by the page across one recount.
 *
 * Members only. The bar draws both shares against the CURRENT universe, because a delta measured
 * against two different denominators is not a delta — carrying the old universe would only invite a
 * reader to use it.
 */
export interface PreviousCount {
  members: number;
}

export interface AudienceSizeBarProps {
  state: CountState;
  /** The count is real, but for a definition that has since changed — mark it and offer a recount. */
  stale?: boolean;
  /** The size as it read before the most recent recount, when this session saw one. */
  previous?: PreviousCount | null;
  /** When the displayed count was recorded (ISO). Renders as "counted 2 minutes ago". */
  countedAt?: string | null;
  onCalculate: () => void;
  /** The count is in flight — the refresh shows a spinner and disables. */
  pending: boolean;
  onPreview: () => void;
  /** The preview drawer is open — the control reads as pressed and the icon becomes an X. */
  previewOpen?: boolean;
  /** The sample is in flight — the preview button shows a spinner and disables. */
  previewPending?: boolean;
}

/** members/universe as a percentage; 0 when there is no universe to divide by. */
function shareOf(members: number, universe: number): number {
  return universe === 0 ? 0 : (members / universe) * 100;
}

/**
 * A share as a bar width, rounded to two decimals — `1544 / 20000 * 100` is `7.720000000000001` in
 * binary floating point and that lands in the DOM verbatim. Two decimals is well below one device
 * pixel at any plausible bar width, so nothing visible is lost.
 *
 * No minimum width here or anywhere else — see the segment block below for why the floor was removed.
 */
function barWidth(share: number): string {
  return `${Math.round(Math.min(100, share) * 100) / 100}%`;
}

/** The signed change in members, or `null` when there is nothing to compare or nothing changed. */
function deltaOf(state: CountState, previous: PreviousCount | null | undefined): number | null {
  if (state.status !== 'counted' || !previous) return null;
  const delta = state.members - previous.members;
  return delta === 0 ? null : delta;
}

export function AudienceSizeBar({
  state,
  stale = false,
  previous,
  countedAt,
  onCalculate,
  pending,
  onPreview,
  previewOpen = false,
  previewPending = false,
}: AudienceSizeBarProps): React.ReactElement {
  const counted = state.status === 'counted' ? state : null;
  const delta = deltaOf(state, previous);
  const staleFade = (fill: string): string => cn('h-full', fill, stale && 'opacity-50');

  // Three segments over one track, drawn at TRUE share. `base` is the part both counts agree on, so the
  // bar never jumps — the difference is what gets coloured, in the direction it moved.
  //
  // NO minimum width, on any segment. A floor was tried and removed: raising a tiny audience to a visible
  // 0.75% meant scaling every segment by the same factor to keep them adding up, and that factor is huge
  // exactly when the audience is small. 8 members of 20,000 with a loss of 868 scaled ×18, so the red
  // tail painted four fifths of a track whose real span is 4%. A sliver that reads as zero is honest
  // about a share that rounds to zero, and the number and the delta chip above state it exactly.
  const share = counted ? shareOf(counted.members, counted.universe) : 0;
  const previousShare = counted && previous ? Math.min(shareOf(previous.members, counted.universe), 100) : share;
  const base = Math.min(share, previousShare);
  const gained = Math.max(0, share - previousShare);
  // The removed part trails the new size, so this one segment extends past the fill — that is the picture
  // of a narrowing. `previousShare` is clamped above, so the track still cannot overflow.
  const lost = Math.max(0, previousShare - share);

  return (
    // `p-4` (not a padding-less edge): the refresh button draws a 2px interaction ring that a flush
    // container would clip.
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-xs font-bold tracking-wider text-muted-foreground uppercase">
            <Users className="size-3.5" aria-hidden />
            Audience size
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {counted ? (
              <span className="text-3xl leading-none font-semibold text-foreground tabular-nums">
                {formatGrouped(counted.members)}
              </span>
            ) : (
              // `not-run` and `error` have no number to show. CountBadge already spells both — "Unknown",
              // with the warehouse's message on a danger icon — so the two surfaces cannot disagree.
              <CountBadge state={state} icon={null} className="text-lg font-medium text-foreground" />
            )}
            {delta !== null ? (
              <Badge variant={delta > 0 ? 'success' : 'error'} bordered>
                {/* The direction in WORDS for a screen reader. On screen it is carried by the badge's
                    colour and by a U+2212 minus sign, and neither survives being read aloud reliably —
                    a listener heard the same phrase for a gain and a loss. The number keeps its own
                    element so the visible text stays exactly "+124" / "−256". */}
                <span className="sr-only">{delta > 0 ? 'increased by' : 'decreased by'}</span>
                <span>
                  {delta > 0 ? '+' : '−'}
                  {formatGrouped(Math.abs(delta))}
                </span>
              </Badge>
            ) : null}
            {stale ? <StaleIndicator /> : null}
          </div>
        </div>

        {/* A LABELLED button, not an icon with a tooltip explaining it (ACT2-604). The tooltip existed
            for two jobs and lost both: it named the disabled reason while an edit was open, and — since
            an icon-only control says nothing on its own — it named the action itself on hover. Counting
            a draft is now allowed, so there is no reason to name, and visible text beats a tooltip a
            keyboard or touch user may never see. */}
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="tertiary"
            size="md"
            label="Calculate"
            Icon={<RefreshCw />}
            onClick={onCalculate}
            loading={pending}
            disabled={pending}
          />
          <Button
            variant="tertiary"
            size="md"
            label="Preview"
            Icon={previewOpen ? <X /> : <Users />}
            aria-pressed={previewOpen}
            aria-expanded={previewOpen}
            aria-controls={previewOpen ? 'audience-preview-panel' : undefined}
            onClick={onPreview}
            loading={previewPending}
            disabled={previewPending}
          />
        </div>
      </div>

      {counted ? (
        <>
          <div
            className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={Math.round(share * 10) / 10}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${formatGrouped(counted.members)} of ${formatGrouped(counted.universe)} members`}
          >
            {/* `stale` fades EVERY segment, not only the base. A stale count describes a definition that
                is no longer on screen, and its delta describes it just as much — leaving the coloured
                tail at full strength made the least trustworthy part of the picture the brightest.
                The page also withholds `previous` while stale, so the two normally cannot appear
                together; this keeps the component honest on its own, for any caller. */}
            <span className={staleFade('bg-primary')} style={{ width: barWidth(base) }} />
            {gained > 0 ? <span className={staleFade('bg-success')} style={{ width: barWidth(gained) }} /> : null}
            {lost > 0 ? <span className={staleFade('bg-danger')} style={{ width: barWidth(lost) }} /> : null}
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {counted.percentage.toFixed(1)}% of {formatGrouped(counted.universe)}
            </span>
            {countedAt && dayjs(countedAt).isValid() ? <span>counted {timeAgo(countedAt)}</span> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
